import { Helmet } from "@/lib/helmet-compat";

interface SEOProps {
  /** Full page title — keep under 60 chars including brand. */
  title: string;
  /** Meta description — keep under 160 chars. */
  description: string;
  /** Absolute canonical URL for this page (e.g. https://modernfenceinstall.com/about). */
  canonical?: string;
  /** Absolute URL to a 1200x630 OG image. Defaults to the brand OG image. */
  ogImage?: string;
  /** Override OG type. Defaults to "website". */
  ogType?: "website" | "article" | "profile";
  /** When true, emits noindex,follow. */
  noindex?: boolean;
  /** Optional JSON-LD objects to embed. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Optional Twitter handle (without @). */
  twitterSite?: string;
  /** Optional override for the social (OG/Twitter) title. Defaults to the page title. */
  ogTitle?: string;
  /** Optional override for the social (OG/Twitter) description. Defaults to the meta description. */
  ogDescription?: string;
}

const DEFAULT_OG = "https://modernfenceinstall.com/og-default.jpg";
const SITE_NAME = "Modern Fence Install";

/**
 * Centralised SEO wrapper. Use on every public page so every route ships with:
 * - Title + meta description
 * - Canonical URL
 * - Open Graph + Twitter card (with branded fallback image)
 * - Optional JSON-LD structured data
 *
 * Keeps every new page SEO-complete by default and avoids drift.
 */
const SEO = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG,
  ogType = "website",
  noindex = false,
  jsonLd,
  twitterSite,
  ogTitle,
  ogDescription,
}: SEOProps) => {
  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  // Standardize: every title carries the brand exactly once.
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const socialTitleRaw = ogTitle?.trim() || title;
  const socialTitle = socialTitleRaw.includes(SITE_NAME)
    ? socialTitleRaw
    : `${socialTitleRaw} | ${SITE_NAME}`;
  const socialDescription = ogDescription?.trim() || description;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      )}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />
      {twitterSite && <meta name="twitter:site" content={`@${twitterSite}`} />}

      {/* JSON-LD */}
      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
